import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Text,
} from '../ui';
import {
  ALL_OPTIONAL,
  NO_OPTIONAL,
  type ConsentChoices,
  type ConsentMethod,
} from '../../lib/consent';
import type { ConsentSnapshot } from '../../lib/consentStore';
import { PRIVACY_POLICY_PATH } from '../../lib/privacyPolicySeo';

export interface PrivacySettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  consent: ConsentSnapshot;
  onSave: (choices: ConsentChoices, method: ConsentMethod) => void;
}

type SettingsFormProps = Pick<PrivacySettingsDialogProps, 'consent' | 'onSave' | 'onOpenChange'>;

/**
 * Ticking a box changes nothing until a button is pressed: Save choices keeps
 * the boxes as they stand, and the other two keep what their names say.
 * Closing the dialog any other way leaves the choice in force alone.
 */
function SettingsForm({ consent, onSave, onOpenChange }: SettingsFormProps) {
  // What is in force now. Before anyone has chosen under the opt-in rule that
  // is nothing, so both boxes start empty.
  const [analytics, setAnalytics] = useState(consent.choices.analytics);
  const [advertising, setAdvertising] = useState(consent.choices.advertising);

  return (
    <>
      <DialogBody className="space-y-5">
        <section>
          <div className="flex items-center gap-2">
            <Text as="span" size="sm" weight="semibold" color="primary">
              Necessary
            </Text>
            <Badge intent="neutral" size="sm">Always on</Badge>
          </div>
          <p className="mt-1 text-xs text-neutral-600">
            Support security, sign-in, saved inquiries, and your privacy preferences.
          </p>
        </section>

        <section>
          <Checkbox
            checked={analytics}
            onChange={(event) => setAnalytics(event.target.checked)}
            label={<span className="font-semibold text-primary">Analytics</span>}
            description="Help me understand which pages people visit and how they find the site. Form responses aren’t included."
          />
        </section>

        <section>
          {/* Global Privacy Control keeps this off whatever is saved (honorGpc), so the box says so. */}
          <Checkbox
            checked={advertising && !consent.gpc}
            disabled={consent.gpc}
            onChange={(event) => setAdvertising(event.target.checked)}
            label={<span className="font-semibold text-primary">Advertising measurement</span>}
            description={
              consent.gpc
                ? 'Help me see whether ads lead to inquiries. Off because your browser sends Global Privacy Control, which asks sites not to share your information for advertising.'
                : 'Help me see whether ads lead to inquiries. Requires Analytics. Never used to personalize ads.'
            }
          />
        </section>

        <p className="text-xs text-neutral-600">
          You can change your choices anytime using Privacy settings in the footer.
        </p>
      </DialogBody>

      <DialogFooter className="flex-wrap sm:justify-between">
        <Link
          to={PRIVACY_POLICY_PATH}
          onClick={() => onOpenChange(false)}
          className="rounded-lg text-sm font-medium text-neutral-600 underline underline-offset-4 hover:text-neutral-800 focus:outline-none focus:ring-2 focus:ring-accent"
        >
          Privacy policy
        </Link>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={() => onSave(NO_OPTIONAL, 'reject_optional')}>
            Reject optional
          </Button>
          <Button variant="secondary" size="sm" onClick={() => onSave(ALL_OPTIONAL, 'accept_all')}>
            Accept all
          </Button>
          <Button variant="primary" size="sm" onClick={() => onSave({ analytics, advertising }, 'custom')}>
            Save choices
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}

/**
 * Every optional purpose on its own switch, with what it does. Opened from the
 * banner's Customize and from the Privacy settings link; it is also where a
 * choice is withdrawn.
 */
export function PrivacySettingsDialog({ open, onOpenChange, consent, onSave }: PrivacySettingsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Privacy settings</DialogTitle>
          <DialogDescription>Choose which optional cookies to allow.</DialogDescription>
        </DialogHeader>
        {/* Mounted per opening, so the boxes start from the choice in force. */}
        {open && <SettingsForm consent={consent} onSave={onSave} onOpenChange={onOpenChange} />}
      </DialogContent>
    </Dialog>
  );
}
